import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, lazy, Suspense } from "react";
import CookieBanner from "@/components/layout/CookieBanner";
import PageTracker from "@/components/PageTracker";
import { ScrollReveal } from "@/components/ScrollReveal";
import ChristmasDecorations from "@/components/ChristmasDecorations";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Public Pages
import HomePage from "@/pages/HomePage";
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const EventDetailPage = lazy(() => import("@/pages/EventDetailPage"));
const EventSlugRedirect = lazy(() => import("@/pages/EventSlugRedirect"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const ConfirmationPage = lazy(() => import("@/pages/ConfirmationPage"));
const NegozioPage = lazy(() => import("@/pages/NegozioPage"));
const CatalogPage = lazy(() => import("@/pages/CatalogPage"));
const DidatticaPage = lazy(() => import("@/pages/DidatticaPage"));
const RistoranteAgricoloPage = lazy(() => import("@/pages/RistoranteAgricoloPage"));
const SoggiorniPage = lazy(() => import("@/pages/SoggiorniPage"));
const AziendaAgricolaPage = lazy(() => import("@/pages/AziendaAgricolaPage"));
const ChiSiamoPage = lazy(() => import("@/pages/ChiSiamoPage"));
const ContattiPage = lazy(() => import("@/pages/ContattiPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));

// Admin Pages
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminEvents = lazy(() => import("@/pages/admin/AdminEvents"));
const AdminBookings = lazy(() => import("@/pages/admin/AdminBookings"));
const CheckInPage = lazy(() => import("@/pages/admin/CheckInPage"));
const AdminCalendar = lazy(() => import("@/pages/admin/AdminCalendar"));
const AdminFormFields = lazy(() => import("@/pages/admin/AdminFormFields"));
const AdminImages = lazy(() => import("@/pages/admin/AdminImages"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminContacts = lazy(() => import("@/pages/admin/AdminContacts"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminFiles = lazy(() => import("@/pages/admin/AdminFiles"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminContentBlocks = lazy(() => import("@/pages/admin/AdminContentBlocks"));
const AdminGallery = lazy(() => import("@/pages/admin/AdminGallery"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminEmailTemplates = lazy(() => import("@/pages/admin/AdminEmailTemplates"));
const AdminEmailMarketing = lazy(() => import("@/pages/admin/AdminEmailMarketing"));
const AdminSiteSettings = lazy(() => import("@/pages/admin/AdminSiteSettings"));
const AdminCatalogs = lazy(() => import("@/pages/admin/AdminCatalogs"));
const AdminMessages = lazy(() => import("@/pages/admin/AdminMessages"));
const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));

function RouteFallback() {
  return (
    <div className="route-fallback" data-testid="route-loading">
      <div className="spinner" />
    </div>
  );
}

function AnimatedRoutes({ children }) {
  const { pathname } = useLocation();
  return <div key={pathname} className="page-transition">{children}</div>;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-background grain-overlay">
        <BrowserRouter>
          <ScrollToTop />
          <PageTracker />
          <ScrollReveal />
          <ChristmasDecorations />
          <Suspense fallback={<RouteFallback />}>
          <AnimatedRoutes>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/esperienze" element={<EventsPage />} />
            <Route path="/esperienze/:eventId" element={<EventDetailPage />} />
            {/* SEO-friendly slug URL: /esperienza/corso-bbq-abc12345 -> resolve to event id */}
            <Route path="/esperienza/:slug" element={<EventSlugRedirect />} />
            {/* Redirect old /eventi URLs */}
            <Route path="/eventi" element={<Navigate to="/esperienze" replace />} />
            <Route path="/eventi/:eventId" element={<Navigate to="/esperienze" replace />} />
            <Route path="/prenota/:eventId" element={<BookingPage />} />
            <Route path="/conferma/:bookingId" element={<ConfirmationPage />} />
            <Route path="/negozio" element={<NegozioPage />} />
            <Route path="/negozio/catalogo/:slug" element={<CatalogPage />} />
            <Route path="/didattica" element={<DidatticaPage />} />
            <Route path="/ristorante-agricolo" element={<RistoranteAgricoloPage />} />
            <Route path="/soggiorni" element={<SoggiorniPage />} />
            <Route path="/azienda-agricola" element={<AziendaAgricolaPage />} />
            <Route path="/chi-siamo" element={<ChiSiamoPage />} />
            <Route path="/contatti" element={<ContattiPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/newsletter/disiscrivi" element={<UnsubscribePage />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/esperienze" element={<AdminEvents />} />
            <Route path="/admin/prenotazioni" element={<AdminBookings />} />
            <Route path="/admin/checkin" element={<CheckInPage />} />
            <Route path="/admin/calendario" element={<AdminCalendar />} />
            <Route path="/admin/campi-form" element={<AdminFormFields />} />
            <Route path="/admin/immagini" element={<AdminImages />} />
            <Route path="/admin/contenuti" element={<AdminContent />} />
            <Route path="/admin/contatti" element={<AdminContacts />} />
            <Route path="/admin/categorie" element={<AdminCategories />} />
            <Route path="/admin/files" element={<AdminFiles />} />
            <Route path="/admin/recensioni" element={<AdminReviews />} />
            <Route path="/admin/blocchi" element={<AdminContentBlocks />} />
            <Route path="/admin/galleria" element={<AdminGallery />} />
            <Route path="/admin/statistiche" element={<AdminAnalytics />} />
            <Route path="/admin/email" element={<AdminEmailTemplates />} />
            <Route path="/admin/newsletter" element={<AdminEmailMarketing />} />
            <Route path="/admin/stagione" element={<AdminSiteSettings />} />
            <Route path="/admin/cataloghi" element={<AdminCatalogs />} />
            <Route path="/admin/messaggi" element={<AdminMessages />} />
          </Routes>
          </AnimatedRoutes>
          </Suspense>
          <CookieBanner />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </HelmetProvider>
  );
}

export default App;
